import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnerApiService } from '../../services/api';
import { MenuCategory, MenuItem, MENU_ITEM_STATUS_LABELS } from '../../types/api';
import { useCabinet } from '../../contexts/CabinetContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { getImageUrl } from '../../utils/image';
import { formatTenge } from '../../utils/money';
import { useLanguage } from '../../contexts/LanguageContext';
import { CategoryTree } from '../catalog/CategoryTree';
import { itemDiscountLabel } from '../discount/discountLabels';
import {
  CategoryNode,
  buildCategoryTree,
  categoryPathLabel,
  flattenCategoryTree,
  isLeafCategory,
} from '../../utils/categoryTree';

/** Позиция вместе с разрешёнными данными из соседних сущностей. */
interface CatalogRow {
  item: MenuItem;
  categoryId: number;
  categoryName: string;
}

const TABLE_COLUMNS = 'minmax(220px, 2.4fr) minmax(120px, 1fr) 110px 118px 92px';

/** Позиций на странице. Больше сотни в таблице всё равно не читают. */
const PER_PAGE = 50;

export const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { query, refreshDashboard } = useCabinet();
  const { t } = useLanguage();

  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<number | 'all'>('all');

  // Поиск серверный, поэтому ввод в шапке ждёт паузы: без неё «карбонара» —
  // это девять запросов, из которых восемь устарели ещё до ответа.
  const search = useDebouncedValue(query.trim(), 300);

  /** Категории: нужны чипам и счётчикам, страницу таблицы не трогают. */
  const loadSidecars = useCallback(async () => {
    setCategories(await partnerApiService.getMenuCategories());
  }, []);

  /** Страница позиций. Фильтр, поиск и нарезку считает база. */
  const loadItems = useCallback(async () => {
    const result = await partnerApiService.getMenuItemsPage({
      categoryId: activeCategory === 'all' ? undefined : activeCategory,
      search: search || undefined,
      page,
      perPage: PER_PAGE,
    });
    setItems(result.items);
    setTotal(result.total);
  }, [activeCategory, search, page]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        await Promise.all([loadSidecars(), loadItems()]);
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка загрузки каталога:', err);
        setError('Не удалось загрузить каталог');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSidecars, loadItems]);

  // Смена фильтра или запроса начинает выдачу заново: третья страница
  // прошлого фильтра в новом чаще всего не существует.
  //
  // Сброс идёт в рендере, а не в эффекте. Эффект отработал бы после запроса
  // за третьей страницей нового фильтра — то есть лишний круг в сеть и мигание
  // пустой таблицы перед правильным ответом.
  const filterKey = `${activeCategory}|${search}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
  }

  const reload = useCallback(async () => {
    try {
      setError(null);
      await Promise.all([loadSidecars(), loadItems()]);
    } catch (err) {
      console.error('Ошибка загрузки каталога:', err);
      setError('Не удалось загрузить каталог');
    }
  }, [loadSidecars, loadItems]);

  /**
   * Сообщение из 422: правила иерархии проверяет бэк, и его текст точнее
   * общего «не удалось» — он называет раздел и причину.
   */
  const describeError = (err: unknown, fallback: string): string => {
    const errors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors;
    const first = errors ? Object.values(errors)[0]?.[0] : undefined;

    return first ?? fallback;
  };

  const createCategory = async (name: string, parentId: number | null) => {
    try {
      setError(null);
      await partnerApiService.createMenuCategory({ name, parent_id: parentId });
      await reload();
      refreshDashboard();
    } catch (err) {
      console.error('Ошибка создания категории:', err);
      setError(describeError(err, 'Не удалось создать категорию'));
    }
  };

  /**
   * Разбиение наполненного раздела: подраздел заводится вместе с переносом
   * туда всех его товаров. Другого способа добавить вложенность в уже
   * собранный каталог нет — товары лежат только в конечных разделах.
   */
  const splitCategory = async (category: MenuCategory, name: string) => {
    try {
      setError(null);
      await partnerApiService.splitMenuCategory(category.id, name);
      await reload();
      refreshDashboard();
    } catch (err) {
      console.error('Ошибка разбиения раздела:', err);
      setError(describeError(err, 'Не удалось разбить раздел'));
    }
  };

  /** Переименование и перенос идут одним запросом. */
  const saveCategory = async (category: MenuCategory, name: string, parentId: number | null) => {
    try {
      setError(null);
      await partnerApiService.updateMenuCategory(category.id, { name, parent_id: parentId });
      await reload();
      refreshDashboard();
    } catch (err) {
      console.error('Ошибка изменения категории:', err);
      setError(describeError(err, 'Не удалось изменить раздел'));
    }
  };

  const removeCategory = async (category: MenuCategory, node: CategoryNode) => {
    const subtree = flattenCategoryTree([node]);
    const inside = subtree.reduce((sum, item) => sum + (item.menu_items_count ?? 0), 0);
    const branches = subtree.length - 1;

    const warning = [
      branches > 0 ? `Вместе с ним исчезнут подразделы: ${branches}.` : '',
      inside > 0 ? `Позиций внутри: ${inside}.` : '',
    ]
      .filter(Boolean)
      .join(' ');

    if (!window.confirm(`Удалить раздел «${category.name}»? ${warning}`.trim())) return;

    try {
      setError(null);
      // Каскад — только по подтверждению выше: без него бэк не тронет ветку.
      await partnerApiService.deleteMenuCategory(category.id, branches > 0);
      if (subtree.some((item) => item.id === activeCategory)) setActiveCategory('all');
      await reload();
      refreshDashboard();
    } catch (err) {
      console.error('Ошибка удаления категории:', err);
      setError(describeError(err, 'Не удалось удалить раздел'));
    }
  };

  // Счётчики берём из ответа категорий, а не из загруженной страницы: на
  // странице лежит срез выдачи, и «Напитки 12» превратилось бы в «Напитки 3».
  const totalItems = useMemo(
    () => categories.reduce((sum, category) => sum + (category.menu_items_count ?? 0), 0),
    [categories],
  );

  // Подпись раздела — весь путь: в таблице «Чаи» встречается и в баре, и в
  // магазине, и по одному названию строку не опознать.
  const categoryLabels = useMemo(
    () => new Map(categories.map((category) => [category.id, categoryPathLabel(category, categories)])),
    [categories],
  );

  const selectedCategory =
    activeCategory === 'all' ? null : categories.find((category) => category.id === activeCategory) ?? null;

  /**
   * Куда вести по кнопке «+ Позиция».
   *
   * Товары кладутся только в конечные разделы, поэтому у ветки берётся её
   * первый конечный раздел: партнёр, выбравший «Бар», всё равно попадёт туда,
   * где позицию можно завести.
   */
  const targetLeafId = useMemo<number | undefined>(() => {
    const leaves = categories.filter((category) => isLeafCategory(category, categories));
    if (leaves.length === 0) return undefined;
    if (!selectedCategory) return leaves[0].id;

    const branch = flattenCategoryTree(buildCategoryTree(categories)).find(
      (node) => node.id === selectedCategory.id,
    );
    const inside = branch ? flattenCategoryTree([branch]) : [];

    return inside.find((node) => isLeafCategory(node, categories))?.id ?? leaves[0].id;
  }, [categories, selectedCategory]);

  const rows = useMemo<CatalogRow[]>(
    () =>
      items.map((item) => ({
        item,
        categoryId: item.menu_category_id,
        categoryName: categoryLabels.get(item.menu_category_id) ?? '—',
      })),
    [items, categoryLabels],
  );

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

  if (isLoading) {
    return (
      <>
        <div className="qp-skel" style={{ height: 44 }} />
        <div className="qp-skel" style={{ height: 420 }} />
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h1 className="qp-label">{t.cabinet.catalogTitle}</h1>
        {selectedCategory && (
          // Хлебные крошки: в дереве видно место раздела, в таблице — нет.
          <span className="qp-cell-sub">{categoryPathLabel(selectedCategory, categories)}</span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="qp-btn qp-btn--primary"
            disabled={!targetLeafId}
            title={targetLeafId ? undefined : t.cabinet.firstCreateCategory}
            onClick={() => {
              if (targetLeafId) navigate(`/catalog/category/${targetLeafId}`);
            }}
          >
            {t.cabinet.addItem}
          </button>
        </div>
      </div>

      {error && <div className="qp-notice qp-notice--error">{error}</div>}

      <div className="qp-catalog">
        <CategoryTree
          categories={categories}
          selectedId={activeCategory}
          totalItems={totalItems}
          onSelect={setActiveCategory}
          onCreate={createCategory}
          onSplit={splitCategory}
          onSave={saveCategory}
          onDelete={removeCategory}
        />

        {categories.length === 0 ? (
        <div className="qp-empty">
          <h3>{t.cabinet.emptyCatalogTitle}</h3>
          <p>{t.cabinet.emptyCatalogDesc}</p>
        </div>
      ) : (
        <div className="qp-table">
          <div className="qp-tr qp-thead" style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: 760 }}>
            <span>{t.cabinet.item}</span>
            <span>{t.cabinet.category}</span>
            <span className="qp-td-right">{t.cabinet.price}</span>
            <span className="qp-td-right">{t.cabinet.priceWithDiscount}</span>
            <span className="qp-td-right">{t.cabinet.discount}</span>
          </div>

          {rows.length === 0 ? (
            <div className="qp-tfoot" style={{ justifyContent: 'center', padding: '32px 16px' }}>
              {search || activeCategory !== 'all'
                ? t.cabinet.nothingFound
                : t.cabinet.noItemsYet}
            </div>
          ) : (
            rows.map((row) => {
              const cover = row.item.images?.[0];
              const coverUrl = cover ? getImageUrl(cover.url) || cover.url : null;
              // Скидку и цену считает бэк — фронт больше не пытается угадать
              // расписание заведения и его «сейчас». Подпись — в единице
              // скидки: «−500 ₸» у тенге, «−20 %» у процента (ADR-026).
              const badge = itemDiscountLabel(row.item);
              // Сравнение с null, а не на истинность: при скидке 100 % цена
              // 0 ₸ — это цена, а не её отсутствие.
              const discounted = badge != null ? row.item.price_with_discount ?? null : null;
              const status = row.item.status ?? 'active';

              return (
                <button
                  key={row.item.id}
                  type="button"
                  className={`qp-tr qp-tr--link${status === 'active' ? '' : ' qp-tr--muted'}`}
                  style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: 760 }}
                  onClick={() => navigate(`/catalog/category/${row.categoryId}`)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                    <span className="qp-thumb">{coverUrl && <img src={coverUrl} alt="" loading="lazy" />}</span>
                    {/* Описание — вторая строка, а не отдельная колонка: оно
                        принадлежит позиции и в собственной колонке пустовало бы
                        у большинства строк. */}
                    <span className="qp-cell-stack">
                      <span className="qp-cell-title">{row.item.name}</span>
                      {/* Снятая позиция подписана явно: партнёр должен видеть,
                          почему её нет в приложении, не открывая форму. */}
                      <span className="qp-cell-sub">
                        {status === 'active'
                          ? row.item.description
                          : `${MENU_ITEM_STATUS_LABELS[status]}${row.item.description ? ` · ${row.item.description}` : ''}`}
                      </span>
                    </span>
                  </span>
                  <span className="qp-cell-sub">{row.categoryName}</span>
                  <span
                    className="qp-mono qp-td-right"
                    style={{
                      fontSize: 12.5,
                      color: discounted != null ? 'var(--qp2-faint)' : 'var(--qp2-text)',
                      textDecoration: discounted != null ? 'line-through' : 'none',
                    }}
                  >
                    {formatTenge(row.item.price)}
                  </span>
                  <span
                    className="qp-mono qp-td-right"
                    style={{ fontSize: 12.5, color: discounted != null ? 'var(--qp2-jade)' : 'var(--qp2-faint)' }}
                  >
                    {discounted != null ? formatTenge(discounted) : '—'}
                  </span>
                  <span className="qp-td-right">
                    {badge != null ? (
                      // «Сейчас» — это не то же, что «скидка настроена»:
                      // счастливые часы вечером, а каталог смотрят днём.
                      <span
                        className="qp-status qp-status--ok"
                        title={
                          row.item.discount_active_now
                            ? 'Скидка действует прямо сейчас'
                            : 'Скидка настроена, но сейчас не действует'
                        }
                      >
                        {badge}
                        {row.item.discount_active_now ? ' •' : ''}
                      </span>
                    ) : (
                      <span className="qp-status">{t.cabinet.noDiscount}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}

          <div className="qp-tfoot">
            <span>
              {t.cabinet.shown} {rows.length} {t.cabinet.outOf} {total}
              {total > 0 ? ` ${t.cabinet.positionsCount}` : ''}
            </span>

            {pageCount > 1 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
                <button
                  type="button"
                  className="qp-btn qp-btn--outline qp-btn--sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  {t.common.back}
                </button>
                <span className="qp-mono" style={{ fontSize: 12 }}>
                  {page} / {pageCount}
                </span>
                <button
                  type="button"
                  className="qp-btn qp-btn--outline qp-btn--sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
                >
                  {t.common.next}
                </button>
              </span>
            )}

          </div>
        </div>
      )}
      </div>
    </>
  );
};
